import React from 'react';
import { motion } from 'framer-motion';
import { Award, BadgeCheck, ShieldCheck, Cpu } from 'lucide-react';

const Certifications = () => {
  const certificationCategories = [
    {
      title: 'Cybersecurity',
      icon: <ShieldCheck size={20} />,
      certifications: [
        { name: 'Certified in Cybersecurity (CC)', issuer: 'ISC2', date: 'Feb 2024', details: 'Certification #1792679' },
        { name: 'Network Security Practitioner (CNSP)', issuer: 'The SecOps Group', date: 'Aug 2024', details: 'Passed with Merit' },
        { name: 'Ethical Hacking Essentials (EHE)', issuer: 'EC-Council', date: 'Feb 2023' },
        { name: 'Fortinet Fundamentals (FCF)', issuer: 'Fortinet', date: 'Mar 2024' },
        { name: 'NSE 1 Network Security Associate', issuer: 'Fortinet', date: 'Sep 2022' },
        { name: 'Introduction to Cybersecurity', issuer: 'Cisco', date: 'Apr 2024' }
      ]
    },
    {
      title: 'Programming & CS Foundations',
      icon: <Cpu size={20} />,
      certifications: [
        { name: 'Programming in C', issuer: 'NPTEL Elite', date: 'Apr 2023', details: 'Elite Certificate' },
        { name: 'DSA Using Java', issuer: 'NPTEL', date: 'Oct 2023', details: '12-week program' },
        { name: 'Operating System Fundamentals', issuer: 'NPTEL', date: 'Oct 2024' },
        { name: 'Database Management Systems', issuer: 'NPTEL', date: 'Sep 2023' },
        { name: 'MongoDB Node.js Path', issuer: 'MongoDB', date: 'Feb 2024' }
      ]
    },
    {
      title: 'Academic & Recognition',
      icon: <BadgeCheck size={20} />,
      certifications: [
        { name: 'NPTEL Discipline Star', issuer: 'NPTEL', date: 'Dec 2024', details: 'Top 1% Nationwide' },
        { name: 'GPCSSI 2024 Internship', issuer: 'Haryana Police', date: 'Jul 2024', details: 'Cyber Warrior Certificate' },
        { name: 'Hack IITK Challenge', issuer: 'IIT Kanpur', date: 'Jan 2025' }
      ]
    }
  ];

  return (
    <section id="certifications" className="py-24 bg-white dark:bg-black transition-colors duration-300">
      <div className="container mx-auto px-6 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-white mb-4 font-inter">
            Certifications
          </h2>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto font-inter">
            Professional validation and academic achievements in security and development.
          </p>
        </motion.div>

        <div className="space-y-16">
          {certificationCategories.map((category, catIdx) => (
            <div key={category.title}>
              <div className="flex items-center gap-3 mb-8">
                <div className="p-2 rounded-xl bg-gray-50 dark:bg-gray-800 text-blue-600 dark:text-blue-400">
                  {category.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white font-inter">
                  {category.title}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.certifications.map((cert, idx) => (
                  <motion.div
                    key={cert.name}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.05, duration: 0.4 }}
                    className="apple-card p-6 flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex justify-between items-start mb-4">
                        <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest px-2 py-1 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                          {cert.issuer}
                        </span>
                        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-tighter">
                          {cert.date}
                        </span>
                      </div>
                      <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2 leading-tight font-inter">
                        {cert.name}
                      </h4>
                      {cert.details && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 font-inter">
                          {cert.details}
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Professional Note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mt-24 p-8 rounded-[32px] bg-gray-50 dark:bg-[#1c1c1e] text-center border border-gray-100 dark:border-gray-800"
        >
          <div className="flex justify-center mb-6">
            <Award className="text-blue-600 dark:text-blue-400" size={40} />
          </div>
          <p className="text-gray-600 dark:text-gray-300 font-inter text-lg max-w-3xl mx-auto">
            Continuously expanding technical horizons through professional training, 
            academic rigorous programs, and industry-standard validations.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Certifications;
